import { EnumLanguage } from '@libs/constants';
import { Util } from '@libs/utilities/util';

import { EnumMbaProduct } from '../../contracts/mba-amazon.constants';
import { IAmazonMbaMarket, MBA_MARKETPLACE_SPECIFICATION } from '../../contracts/mba-marketplace.constants';
import { IAmazonMbaMarketplace, IAmazonMbaProduct, MBA_PRODUCT_SPECIFICATION } from '../../contracts/mba-products.constants';
import { IPodDesign } from '../../contracts/pod-design.interface';
import { IMbaProduct, IMbaText, IPodListingMba } from '../../contracts/pod-listing.interface';
import { EnumPlatform } from '../../contracts/print-on-demand.constants';
import { IUploadPodListingLimitations, UPLOAD_CONFIG } from '../../upload.config';
import { IAbstractStatus, IListingCheck, IPlatformStatus, PLACEHOLDERS } from '../data/listing-check.interface';


export class MbaListingCheckHelper implements IListingCheck {

	//** Configurations */
	private readonly LISTING_LIMITATIONS: IUploadPodListingLimitations = UPLOAD_CONFIG.POD.LISTING_LIMITATIONS;
	readonly BAD_CHARACTER_REGEX: RegExp = /[^ -)+-­¯-ÿẞ€ŽŠšžŒœŸ一-龠ぁ-んゔァ-ヴー々〆〤ａ-ｚＡ-Ｚ０-９①-⑳、-］「」°　–…]/g; // eslint-disable-line no-irregular-whitespace


	/**------------------------------------------------------
	 * Check for empty listing data
	 */
	isListingEmpty(data: IPodDesign): boolean {

		//0 - is any mba data available
		if (data?.listing?.mba === null || typeof data?.listing?.mba === 'undefined') return true;

		//1 - is any listing text available
		for (const text of data.listing.mba.texts) {
			if (!Util.String.areEmpty([text.brand, text.title, text.bullet1, text.bullet2, text.description])) return false;
		}

		//2 - all listing text was empty
		return true;
	}


	/**------------------------------------------------------
	 * Merch By Amazon
	 * ---------------
	 * Brand
	 *  - required, min 3 char, max 50 char
	 * Title
	 *  - required, min 3 char, max 60 char
	 * Bullet 1 & Bullet 2
	 *  - optional (NOT required), max 256 char
	 * Description
	 *  - optional (NOT required)
	 *  - if desc. is added it has to be min 75 char
	 *  - max 2000 char
	 */
	checkListing(data: IPodDesign): IPlatformStatus {

		//0 - check if any merch by amazon data was defined
		const status: IPlatformStatus = { code: EnumPlatform.MBA, warnings: [], errors : [] };
		if (this.isListingEmpty(data) || !data.listing.mba) {
			status.errors.push('merch by amazon listing is empty');
			return status;
		}

		//1 - is any product selected?
		const products: IMbaProduct[] = data.listing.mba.products;
		if (products.length === 0) {
			status.errors.push('no products configured');
			return status;											// no need to check further
		}

		//2 - is any marketplace selected?
		let marketCount: number = 0;
		for (const product of products) marketCount += product.marketplaces.length;
		if (marketCount === 0) {
			status.errors.push('no marketplace configured');
			return status;											// no need to check further
		}

		//3 - check all the product individually
		for (const product of products) this.checkProduct(product, status);

		//4 - check the texts/listing
		this.checkTexts(data.listing.mba, status);

		return status;
	}


	/**------------------------------------------------------
	 * Check MBA Products
	 */
	isProductMarketplaceEmpty(product: IMbaProduct): boolean {
		return product.marketplaces.length === 0;				// if marketplaces are selected, it is intended for uploading
	}

	checkProductWithListing(mbaListing: IPodListingMba, product: IMbaProduct): IMbaProductStatus {

		//0 - check the product options
		const productStatus: IMbaProductStatus = { code: product.productCode, warnings: [], errors: [] };
		this.checkProduct(product, productStatus);

		//1 - check the listing based on the current product
		this.checkTexts({ ...mbaListing, products: [product] }, productStatus);

		return productStatus;
	}


	/**------------------------------------------------------
	 * Extract the Required Languages
	 */
	getRequiredLanguages(products: IMbaProduct[]): EnumLanguage[] {

		//0 - define the language result array
		const requiredLanguages: EnumLanguage[] = [];

		//1 - loop over all products to find the required languages, based on the markets
		for (const product of products) {
			for (const market of product.marketplaces) {

				//a. check if marketplace exists
				const langMap = MBA_MARKETPLACE_SPECIFICATION.find((elem: IAmazonMbaMarket) => elem.code === market.market);
				if (!langMap) throw new Error(`MbaListingCheckService => getAllRequiredLanguages => FATAL ERROR: invalid marketplace code: ${market.market}, mapping element does not exist`);

				//b. add language of market to the required languages
				if (!requiredLanguages.includes(langMap.language)) requiredLanguages.push(langMap.language);
			}
		}
		return requiredLanguages;
	}


	/**------------------------------------------------------
	 * Helper Function
	 */
	private checkProduct(product: IMbaProduct, status: IAbstractStatus): IAbstractStatus {

		//0 - get the product configuration
		const productConfig = MBA_PRODUCT_SPECIFICATION.find((elem: IAmazonMbaProduct) => elem.code === product.productCode);
		if (!productConfig) throw new Error(`MbaListingCheckService => checkProduct => FATAL ERROR: product specification not found for "${product.productCode}"`);

		//1 - was any marketplace selected?
		// > Note: if not there is intention to upload the product to amazon
		if (this.isProductMarketplaceEmpty(product)) return status;

		//2 - check the price in the marketplace
		for (const market of product.marketplaces) {

			//a. get the marketplace configuration
			const marketConfig = productConfig.marketplaces.find((elem: IAmazonMbaMarketplace) => elem.market === market.market);
			if (!marketConfig) throw new Error(`MbaListingCheckService => checkProduct => FATAL ERROR: market specification not found for "${market.market}"`);

			//b. check if the price is in range of min and max
			if (market.price < marketConfig.priceMin) status.errors.push(`price for "${productConfig.name}/${marketConfig.market}" under minimum of "${marketConfig.priceMin}"`);
			if (market.price > marketConfig.priceMax) status.errors.push(`price for "${productConfig.name}/${marketConfig.market}" over minimum of "${marketConfig.priceMax}"`);
		}

		//3 - was any type selected (if required)
		if (productConfig?.fitTypes?.editable && product.fitTypes.length === 0) status.errors.push(`no selected fit types for "${productConfig.name}"`);

		//4 - was at least one color selected
		if (productConfig.colors.all.length !== 0 && product.colors.length === 0) status.errors.push(`no selected color for "${productConfig.name}"`);

		//5 - are the colors exceeding it's limit
		if (productConfig.colors.all.length !== 0 && product.colors.length > this.LISTING_LIMITATIONS.MBA.COLORS) status.errors.push(`selected colors for "${productConfig.name} exceeds more than ${this.LISTING_LIMITATIONS.MBA.COLORS}"`);

		return status;
	}

	// Preserve the existing platform validation branches during extraction.
	// eslint-disable-next-line complexity
	private checkTexts(mbaListing: IPodListingMba, status: IAbstractStatus): IAbstractStatus {

		//0 - is the text for all required languages provided
		const requiredLanguages: EnumLanguage[] = this.getRequiredLanguages(mbaListing.products);
		for (const text of mbaListing.texts) {

			//a. check if text is required (if not no need to check it)
			const notRequiredByAutoTranslate: boolean = Util.String.isNotEmpty(mbaListing.options.autoTranslate) && text.language !== mbaListing.options.autoTranslate;
			if (!requiredLanguages.includes(text.language) || notRequiredByAutoTranslate) continue;

			//b. check for placeholders
			this.checkForPlaceholdersInListingText(text, status);

			//c. check if any bad/forbidden characters are in the text
			if (this.findBadCharacters(text.brand)) 		status.errors.push(`bad character ${(this.findBadCharacters(text.brand) || []).join(`", "`)} found in brand (${text.language.toLowerCase()})`);
			if (this.findBadCharacters(text.title)) 		status.errors.push(`bad character ${(this.findBadCharacters(text.title) || []).join(`", "`)} found in title (${text.language.toLowerCase()})`);
			if (this.findBadCharacters(text.bullet1)) 	 	status.errors.push(`bad character ${(this.findBadCharacters(text.bullet1) || []).join(`", "`)} found in bulletpoint 1 (${text.language.toLowerCase()})`);
			if (this.findBadCharacters(text.bullet2)) 		status.errors.push(`bad character ${(this.findBadCharacters(text.bullet2) || []).join(`", "`)} found in bulletpoint 2 (${text.language.toLowerCase()})`);
			if (this.findBadCharacters(text.description)) 	status.errors.push(`bad character ${(this.findBadCharacters(text.description) || []).join(`", "`)} found in description (${text.language.toLowerCase()})`);

			//d. check brand
			if (text.brand.length < 3)										status.errors.push(`brand shorter than 3 chars (${text.language.toLowerCase()})`);
			if (text.brand.length > this.LISTING_LIMITATIONS.MBA.BRAND)		status.errors.push(`brand longer than ${this.LISTING_LIMITATIONS.MBA.BRAND} chars (${text.language.toLowerCase()})`);

			//e. check title
			if (text.title.length < 3) 										status.errors.push(`title shorter than 3 chars (${text.language.toLowerCase()})`);
			if (text.title.length > this.LISTING_LIMITATIONS.MBA.TITLE)		status.errors.push(`title longer than ${this.LISTING_LIMITATIONS.MBA.TITLE} chars (${text.language.toLowerCase()})`);

			//f. check both bulletpoint
			if (text.bullet1.length > this.LISTING_LIMITATIONS.MBA.BULLET1) status.errors.push(`bulletpoint 1 longer than ${this.LISTING_LIMITATIONS.MBA.BULLET1} chars (${text.language.toLowerCase()})`);
			if (text.bullet2.length > this.LISTING_LIMITATIONS.MBA.BULLET2) status.errors.push(`bulletpoint 2 longer than ${this.LISTING_LIMITATIONS.MBA.BULLET2} chars (${text.language.toLowerCase()})`);

			//g. check description
			if (!Util.String.isEmpty(text.description) && text.description.length < 75)	status.errors.push(`description shorter than 75 chars (${text.language.toLowerCase()})`);
			if (text.description.length > this.LISTING_LIMITATIONS.MBA.DESCRIPTION) 	status.errors.push(`description longer than ${this.LISTING_LIMITATIONS.MBA.DESCRIPTION} chars (${text.language.toLowerCase()})`);
		}

		return status;
	}

	private checkForPlaceholdersInListingText(text: IMbaText, status: IAbstractStatus) {

		// check if any placeholders are contained in the text
		for (const placeholder of PLACEHOLDERS) {
			if (text.brand.includes(placeholder)) 		status.errors.push(`placeholder "${placeholder}" found in brand (${text.language.toLowerCase()})`);
			if (text.title.includes(placeholder)) 		status.errors.push(`placeholder "${placeholder}" found in title (${text.language.toLowerCase()})`);
			if (text.bullet1.includes(placeholder)) 	status.errors.push(`placeholder "${placeholder}" found in bulletpoint 1 (${text.language.toLowerCase()})`);
			if (text.bullet2.includes(placeholder)) 	status.errors.push(`placeholder "${placeholder}" found in bulletpoint 2 (${text.language.toLowerCase()})`);
			if (text.description.includes(placeholder)) status.errors.push(`placeholder "${placeholder}" found in description (${text.language.toLowerCase()})`);
		}
	}

	private findBadCharacters(text: string): string[] | null {

		//0 - get all bad characters as an array
		const badCharacters: string[] | null = text.match(this.BAD_CHARACTER_REGEX);
		if (!badCharacters) return null;

		//1 - only return unique characters, no duplications
		return Util.Array.unique(badCharacters);
	}
}


//** Interfaces --------------------------------- */
export interface IMbaProductStatus extends IAbstractStatus {
	code		: EnumMbaProduct;		// platform code like for displate', ...
	warnings	: string[];
	errors		: string[];
}
